/** @format */
import { useCallback, useState } from 'react';

type FallbackState = {
    attempt: number;
};

export const useFallback = <T = string>(
    initialSources: T[],
    onError?: (state: { fallbacks: T[]; attempt: number; source: T }) => void
) => {
    // initialSources is considered immutable for simplicity (no risk of infinite loops when resetting state)
    const [sources] = useState<T[]>(initialSources);
    const [fallbackState, setFallbackState] = useState<FallbackState>({
        attempt: 1,
    });

    const nextFallback = useCallback(() => {
        setFallbackState(({ attempt }) => {
            const hasSources = sources.length > 0;
            // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
            const wasLastAttempt = attempt === sources.length;

            onError?.({
                fallbacks: sources,
                attempt,
                source: sources[attempt - 1],
            });

            if (!hasSources || wasLastAttempt) {
                return { attempt };
            }

            return { attempt: attempt + 1 };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [sources]);

    const handleError = useCallback(() => {
        nextFallback();
    }, [nextFallback]);

    const isFallback = fallbackState.attempt > 1;
    const currentSource = sources[fallbackState.attempt - 1];

    return {
        currentSource,
        handleError,
        isFallback,
    };
};
